import type { BossAttackTimeline } from './boss_pose_timeline'

export type FajinMotion = 'old' | 'loaded' | 'settled' | 'vertical' | 'downcut' | 'sparse'

export const FAJIN_RECOVERY_MS: Record<FajinMotion, number> = { old: 140, loaded: 140, settled: 300, vertical: 300, downcut: 300, sparse: 300 }

// 2026-09-12: 溜めの旧2版を保存し、余韻版は復帰のみ140→300ms。
// 溜め・接触・波の時計と当たり判定は共通。
const windupVersions: Record<'old' | 'loaded', BossAttackTimeline> = {
  old: [
    { phase: 'telegraph', at: 0.0, image: 'step_crouch', hold: true },
    { phase: 'telegraph', at: 0.3, image: 'fajin_draw', hold: true, tag: 'anticipation',
      ease: 'easeIn', offset: { x: -30 } },
    { phase: 'telegraph', at: 0.97, image: 'fajin_draw', offset: { x: -38 } },
    { phase: 'active', at: 0.0, image: 'fajin_thrust', hold: true, tag: 'contact',
      ease: 'easeOut', offset: { x: 30 } },
    { phase: 'recovery', at: 0.0, image: 'fajin_thrust', offset: { x: 30 } },
    { phase: 'recovery', at: 0.5, image: 'idle', ease: 'easeOut' },
  ],
  loaded: [
    { phase: 'telegraph', at: 0.0, image: 'step_crouch', hold: true, ease: 'step' },
    { phase: 'telegraph', at: 0.12, image: 'fajin_draw', hold: true, ease: 'step' },
    // 頭の大きさと足裏を合わせた専用絵で沈む。全身の縦潰し・後退で代用しない。
    { phase: 'telegraph', at: 0.30, image: 'fajin_settle', hold: true, ease: 'step' },
    { phase: 'telegraph', at: 0.56, image: 'fajin_coil', hold: true,
      tag: 'anticipation', ease: 'step' },
    // 溜めを固定してから接触へ切り替える。溜めの末尾に突き絵を先出ししない。
    { phase: 'active', at: 0.0, image: 'fajin_thrust', hold: true, tag: 'contact',
      ease: 'easeOut', offset: { x: 30 } },
    { phase: 'recovery', at: 0.0, image: 'fajin_thrust', offset: { x: 30 } },
    { phase: 'recovery', at: 0.5, image: 'idle', ease: 'easeOut' },
  ],
}

export const FAJIN_MOTION_TIMELINES: Record<FajinMotion, BossAttackTimeline> = {
  ...windupVersions,
  // Lab candidate: three drawings animated about the existing foot pivot.
  // Vertical compression stays within 1.5%; small pulses occur just before release.
  sparse: [
    { phase: 'telegraph', at: 0, image: 'idle', hold: true, ease: 'step' },
    { phase: 'telegraph', at: 0.10, image: 'fajin_high', hold: true,
      tag: 'anticipation', ease: 'easeOut', rot: 1 },
    { phase: 'telegraph', at: 0.76, image: 'fajin_high', hold: true,
      ease: 'easeInOut', rot: -1.5, scale: { y: 0.985 } },
    { phase: 'telegraph', at: 0.86, image: 'fajin_high', hold: true,
      rot: -1.5, scale: { y: 0.985 } },
    ...[0.89, 0.92, 0.95].map((at, i) => ({
      phase: 'telegraph' as const, at, image: 'fajin_high', hold: true,
      rot: i % 2 === 0 ? -1.25 : -1.75, scale: { y: 0.985 },
    })),
    { phase: 'telegraph', at: 0.97, image: 'fajin_high', hold: true,
      ease: 'easeIn', rot: -1.5, scale: { y: 0.985 } },
    { phase: 'active', at: 0, image: 'fajin_cut', hold: true,
      tag: 'contact', ease: 'easeOut', offset: { x: 24 } },
    { phase: 'active', at: 0.35, image: 'fajin_finish', hold: true,
      tag: 'followThrough', ease: 'easeOut', offset: { x: 30 }, rot: 1 },
    { phase: 'recovery', at: 0, image: 'fajin_finish', hold: true,
      tag: 'followThrough', ease: 'step', offset: { x: 30 }, rot: 1 },
    { phase: 'recovery', at: 0.30, image: 'fajin_finish', hold: true,
      tag: 'recover', ease: 'easeInOut', offset: { x: 30 }, rot: 1 },
    { phase: 'recovery', at: 0.85, image: 'idle', hold: true,
      ease: 'easeOut', offset: { x: 3 } },
    { phase: 'recovery', at: 1, image: 'idle', hold: true },
  ],
  // 採用された縦振りの専用絵。上段で重さを受ける540msに対し、振り始めは54ms。
  // 波の時計・判定を保ち、振り終わりは186ms残してから戻す。旧仮組みは vertical に保存。
  downcut: [
    { phase: 'telegraph', at: 0, image: 'idle', hold: true, ease: 'step' },
    { phase: 'telegraph', at: 0.10, image: 'fajin_lift', hold: true, ease: 'easeOut' },
    { phase: 'telegraph', at: 0.34, image: 'fajin_high', hold: true,
      tag: 'anticipation', ease: 'easeInOut', offset: { x: -3 } },
    { phase: 'telegraph', at: 0.88, image: 'fajin_high', hold: true,
      ease: 'easeIn', offset: { x: -5 } },
    { phase: 'telegraph', at: 0.94, image: 'fajin_fall', hold: true,
      ease: 'easeIn', offset: { x: 1 } },
    { phase: 'active', at: 0, image: 'fajin_cut', hold: true,
      tag: 'contact', ease: 'easeOut', offset: { x: 24 } },
    { phase: 'active', at: 0.45, image: 'fajin_finish', hold: true,
      tag: 'followThrough', ease: 'step', offset: { x: 32 } },
    { phase: 'recovery', at: 0, image: 'fajin_finish', hold: true,
      tag: 'followThrough', ease: 'step', offset: { x: 32 } },
    { phase: 'recovery', at: 0.40, image: 'fajin_return', hold: true,
      tag: 'recover', ease: 'easeInOut', offset: { x: 32 } },
    { phase: 'recovery', at: 0.90, image: 'idle', hold: true,
      ease: 'easeOut', offset: { x: 3 } },
    { phase: 'recovery', at: 1, image: 'idle', hold: true },
  ],
  // 2026-09-12: 突き以外も検討するユーザー承認案。既存絵だけのラボ用仮組み。
  // 波の位置・形・時計を固定し、正面を上から下へ通す。地面を打つ絵は使わない。
  // horizontal_slash はこの連鎖では振り下ろし途中の刃が正面を通る一瞬に流用。
  vertical: [
    { phase: 'telegraph', at: 0, image: 'idle', hold: true, ease: 'easeInOut' },
    { phase: 'telegraph', at: 0.18, image: 'scoop_through', hold: true,
      ease: 'easeInOut', offset: { x: -6 } },
    { phase: 'telegraph', at: 0.48, image: 'overhead_raise', hold: true,
      tag: 'anticipation', ease: 'step', offset: { x: -6 } },
    { phase: 'telegraph', at: 0.88, image: 'overhead_raise', hold: true,
      ease: 'easeIn', offset: { x: -6 } },
    { phase: 'active', at: 0, image: 'horizontal_slash', hold: true,
      tag: 'contact', ease: 'easeOut', offset: { x: 26 } },
    { phase: 'active', at: 0.45, image: 'fajin_release', hold: true,
      tag: 'followThrough', ease: 'step', offset: { x: 30 } },
    { phase: 'recovery', at: 0, image: 'fajin_release', hold: true,
      tag: 'followThrough', ease: 'step', offset: { x: 30 } },
    { phase: 'recovery', at: 80 / 300, image: 'fajin_release', hold: true,
      tag: 'recover', ease: 'easeInOut', offset: { x: 30 } },
    { phase: 'recovery', at: 230 / 300, image: 'idle', hold: true,
      ease: 'easeOut', offset: { x: 3 } },
    { phase: 'recovery', at: 1, image: 'idle', hold: true },
  ],
  settled: [
    ...windupVersions.loaded.filter(key => key.phase !== 'recovery'),
    // 300msの戻りの中で、一拍受け止める→刃と肩を下ろす→構えを整える。
    // 画像を溶かさず、最初の80msは突き切った姿勢と前方変位を保持。
    { phase: 'recovery', at: 0, image: 'fajin_thrust', hold: true,
      tag: 'followThrough', ease: 'step', offset: { x: 30 } },
    { phase: 'recovery', at: 80 / 300, image: 'fajin_release', hold: true,
      tag: 'recover', ease: 'easeInOut', offset: { x: 30 } },
    { phase: 'recovery', at: 230 / 300, image: 'idle', hold: true,
      ease: 'easeOut', offset: { x: 3 } },
    // stateを離れる前に前方変位をゼロへ。idleへの遷移で位置を飛ばさない。
    { phase: 'recovery', at: 1, image: 'idle', hold: true },
  ],
}
